<template>
  <EquipmentBase :section-id="ScenarioDetailSectionKey.WELL_TEST">
    <EquipmentHeader>
      <template #title>
        <div class="flex white-space-nowrap align-items-center">
          {{ isHps ? "Fluid Type" : "Well Test" }}
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.WELL_TEST]"
          />
        </div>
      </template>
      <template #extra>
        <div class="flex flex-column gap-2 ml-2 lg:flex-row">
          <Calendar
            v-if="!isHps"
            class="pr-6"
            :model-value="wellTestDate"
            show-icon
            date-format="yy-mm-dd"
            @update:model-value="updateWellTestDate($event as Date)"
          />
          <div v-if="isHps" class="flex :gap-2 align-items-center">
            <Dropdown
              v-model="fluidType"
              input-id="fluid-type"
              :disabled="isScenarioReadonly"
              class="w-12rem"
              :options="fluidTypeOptions"
              option-value="value"
              option-label="comment"
              append-to="body"
            />
          </div>

          <div v-if="isEspOnShore" class="flex gap-2 align-items-center">
            <span class="white-space-nowrap heading-2">IPR Model </span>
            <Dropdown
              v-model="parametersForm.data.value.flow_model"
              input-id="flow-model"
              :disabled="isScenarioReadonly || isImportedFromLegacy"
              class="w-12rem"
              :options="flowModels?.flow_model_active"
              optionValue="value"
              optionLabel="comment"
              append-to="body"
            />
          </div>
        </div>
      </template>
    </EquipmentHeader>

    <Divider>Surface</Divider>
    <WellTestSurface
      v-model:totalLiquidFlowRateStbd="totalLiquidFlowRateStbd"
      v-model:waterCutPercentage="waterCutPercentage"
      v-model:oilFlowRateStbd="oilFlowRateStbd"
      v-model:waterFlowRateStbd="waterFlowRateStbd"
      v-model:productivityIndexStbdPsia="productivityIndexStbdPsia"
      v-model:gasOilRatioScfStb="gasOilRatioScfStb"
      v-model:gasLiquidRatioScfStb="gasLiquidRatioScfStb"
      v-model:gasFlowRateMscf="gasFlowRateMscf"
      v-model:gasInputType="gasInputType"
      v-model:liquidInputType="liquidInputType"
      :well-productivity-index-stbd-psia-for-display="
        wellProductivityIndexStbdPsiaForDisplay
      "
      :update-unit="true"
      :form="wellPropertiesForm"
    />

    <Divider>
      {{ isEspOnShore ? "Downhole" : "Pressure Requirements" }}
    </Divider>

    <WellTestDownhole
      v-model:producingBottomholePressurePsia="producingBottomholePressurePsia"
      v-model:staticBottomholePressurePsia="staticBottomholePressurePsia"
      v-model:datumPointFt="datumPointFt"
      v-model:staticFluidLevelFeet="staticFluidLevelFeet"
      v-model:producingFluidLevelFeet="producingFluidLevelFeet"
      @force-datum-point-override="forceDatumPointOverride"
    />
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { injectStrict } from "@/utils/utils";
import {
  Flow_Model_Enum,
  Hps_Liquid_Type_Enum,
  Sizing_Pumping_Type_Enum,
  useFlowModelQuery,
  useHpsLiquidTypesQuery
} from "gql/api";
import {
  getForcedGasTypeForHpsLiquidType,
  getForcedLiquidInputForHpsLiquidType,
  getHpsFlowRateResetState,
  getHpsWaterCutResetState,
  getHpsWaterRateResetState,
  isHpsLiquidTypeWithZeroedFlowRates,
  isHpsLiquidTypeWithZeroedWaterCut,
  isHpsLiquidTypeWithZeroedWaterRate
} from "@/utils/hpsLiquidType";

import WellTestSurface from "@/views/scenarios/wellInfo/WellTestSurface.vue";
import WellTestDownhole from "@/views/scenarios/wellInfo/WellTestDownhole.vue";

import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";

import { useWellTestInput } from "@/composables/forms/useWellTestInput";
import {
  parametersFormKey,
  sizingFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { useEditPermissions } from "@/composables/auth/editPermissions";

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const parametersForm = injectStrict(parametersFormKey);

const { data: flowModels } = useFlowModelQuery();

const gasInputType = computed({
  get() {
    return wellPropertiesForm.data.value?.well_test_gas_input_type;
  },
  set(val) {
    wellPropertiesForm.update({
      well_test_gas_input_type: val
    });
  }
});

const liquidInputType = computed({
  get() {
    return wellPropertiesForm.data.value?.well_test_liquid_input_type;
  },
  set(val) {
    wellPropertiesForm.update({
      well_test_liquid_input_type: val
    });
  }
});

// If no date, just leave it empty instead of generating Dec 31 1969
const wellTestDate = computed(() =>
  wellPropertiesForm.data.value.well_test_test_date
    ? new Date(wellPropertiesForm.data.value.well_test_test_date)
    : undefined
);

// Only date is returned from calendar component with tz offset
// Need to format it to match what PG format so there isn't a false diff
const updateWellTestDate = (
  newDate: string | string[] | Date | Date[] | undefined
) => {
  if (!newDate || !(newDate instanceof Date)) return;
  wellPropertiesForm.update({
    well_test_test_date: newDate.toISOString().replace(".000Z", "+00:00")
  });
};

const { data: hpsLiquidTypes } = useHpsLiquidTypesQuery();
const fluidTypeOptions = computed(
  () => hpsLiquidTypes.value?.hps_liquid_type ?? []
);

const fluidType = computed({
  get() {
    return (
      wellPropertiesForm.data.value?.hps_liquid_type ??
      Hps_Liquid_Type_Enum.Multiphase
    );
  },
  set(val) {
    wellPropertiesForm.update({
      hps_liquid_type: val
    });
  }
});

const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

const sizingForm = injectStrict(sizingFormKey, null);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const isHps = computed(() => {
  return sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const forceDatumPointOverride = (): void => {
  if (
    !wellPropertiesForm.data.value.well_test_datum_point_measured_depth_feet ||
    wellPropertiesForm.data.value.well_test_datum_point_measured_depth_feet ===
      0 ||
    wellPropertiesForm.diff.value.well_test_datum_point_measured_depth_feet ===
      0
  ) {
    // need to update sbhp and pbhp as well
    wellPropertiesForm.update({
      well_test_datum_point_measured_depth_feet:
        wellPropertiesForm.data.value.top_of_perforations_measured_depth_feet
    });
  }
};

// need to watch parametersForm.data.value.flow_model to make sure when hps is selected we have straight line
watch(
  () => parametersForm.data.value.flow_model,
  newFlowModel => {
    if (!isEspOnShore.value && newFlowModel !== Flow_Model_Enum.StraightLine) {
      parametersForm.update({
        flow_model: Flow_Model_Enum.StraightLine
      });
    }
  },
  { immediate: true }
);

const {
  gasFlowRateMscf,
  gasOilRatioScfStb,
  gasLiquidRatioScfStb,
  oilFlowRateStbd,
  waterFlowRateStbd,
  totalLiquidFlowRateStbd,
  waterCutPercentage,
  producingBottomholePressurePsia,
  staticBottomholePressurePsia,
  productivityIndexStbdPsia,
  wellProductivityIndexStbdPsiaForDisplay,
  datumPointFt,
  staticFluidLevelFeet,
  producingFluidLevelFeet
} = useWellTestInput(
  wellPropertiesForm.data,
  parametersForm.data,
  wellPropertiesForm
);

const storedHpsOilFlowRateStbd = ref<number | null>(null);
const storedHpsGasFlowRateMscf = ref<number | null>(null);
const storedHpsWaterFlowRateStbd = ref<number | null>(null);
const storedHpsWaterCutPercentage = ref<number | null>(null);

// Water and amine are forced to oil-rate/water-rate mode in HPS scenarios.
// Condensate is forced wet gas and every non-condensate HPS liquid type resets gas type to dry.
watch(
  () => fluidType.value,
  (newFluidType, previousFluidType) => {
    if (isHps.value) {
      const forcedLiquidInputType =
        getForcedLiquidInputForHpsLiquidType(newFluidType);

      if (forcedLiquidInputType) {
        liquidInputType.value = forcedLiquidInputType;
      }
    }

    if (isHpsLiquidTypeWithZeroedFlowRates(newFluidType)) {
      storedHpsOilFlowRateStbd.value = oilFlowRateStbd.value;
      storedHpsGasFlowRateMscf.value = gasFlowRateMscf.value;
      oilFlowRateStbd.value = 0;
      gasFlowRateMscf.value = 0;
    } else if (
      isHpsLiquidTypeWithZeroedFlowRates(previousFluidType) &&
      storedHpsOilFlowRateStbd.value !== null &&
      storedHpsGasFlowRateMscf.value !== null
    ) {
      const restoredFlowRates = getHpsFlowRateResetState(
        previousFluidType,
        newFluidType,
        storedHpsOilFlowRateStbd.value,
        storedHpsGasFlowRateMscf.value
      );

      oilFlowRateStbd.value = restoredFlowRates.oilFlowRateStbd;
      gasFlowRateMscf.value = restoredFlowRates.gasFlowRateMscf;
    }

    if (isHpsLiquidTypeWithZeroedWaterRate(newFluidType)) {
      storedHpsWaterFlowRateStbd.value = waterFlowRateStbd.value;
      waterFlowRateStbd.value = 0;
      waterCutPercentage.value = 0;
    } else if (
      isHpsLiquidTypeWithZeroedWaterRate(previousFluidType) &&
      storedHpsWaterFlowRateStbd.value !== null
    ) {
      const restoredWaterFlowRate = getHpsWaterRateResetState(
        previousFluidType,
        newFluidType,
        storedHpsWaterFlowRateStbd.value
      );

      waterFlowRateStbd.value = restoredWaterFlowRate.waterFlowRateStbd;
    }

    if (isHpsLiquidTypeWithZeroedWaterCut(newFluidType)) {
      storedHpsWaterCutPercentage.value = waterCutPercentage.value;
      waterCutPercentage.value = 0;
    } else if (
      isHpsLiquidTypeWithZeroedWaterCut(previousFluidType) &&
      storedHpsWaterCutPercentage.value !== null
    ) {
      const restoredWaterCut = getHpsWaterCutResetState(
        previousFluidType,
        newFluidType,
        storedHpsWaterCutPercentage.value
      );

      waterCutPercentage.value = restoredWaterCut.waterCutPercentage;
    }

    if (isHps.value) {
      wellPropertiesForm.update({
        gas_type: getForcedGasTypeForHpsLiquidType(newFluidType)
      });
    }
  },
  { immediate: true }
);
</script>
